import { Command } from "commander";
import { readFileSync, existsSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import Table from "cli-table3";
import { loadData, getModelPricing } from "@fathom/core";
import { warnAction } from "../ux/errors.js";

// ── Types ──────────────────────────────────────────────────

interface SessionRecord {
  sessionId: string;
  featureId?: string;
  confidence: number;
  source: string;
  status: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  model: string;
  sessionStart: number;
  sessionEnd?: number;
  activeMinutes?: number;
  tokenSource: string;
  trackedAt: string;
}

interface ModelPricingRecord {
  id: string;
  provider: string;
  name: string;
  input_per_mtok: number;
  output_per_mtok: number;
  cache_read_per_mtok: number;
  batch_discount: number;
  tier: string;
}

export interface SpendResult {
  total: number;
  inputCost: number;
  outputCost: number;
  cacheCost: number;
  sessionCount: number;
  period: string | null;
  byModel: Array<{
    model: string;
    name: string;
    cost: number;
    sessions: number;
    inputTokens: number;
    outputTokens: number;
  }>;
  byFeature: Array<{
    featureId: string;
    cost: number;
    sessions: number;
  }>;
}

// ── Period parsing ─────────────────────────────────────────

function parsePeriodMs(period: string): number | null {
  const match = period.match(/^(\d+)([dwm])$/);
  if (!match) return null;
  const value = parseInt(match[1], 10);
  const unit = match[2];
  switch (unit) {
    case "d":
      return value * 24 * 60 * 60 * 1000;
    case "w":
      return value * 7 * 24 * 60 * 60 * 1000;
    case "m":
      return value * 30 * 24 * 60 * 60 * 1000;
    default:
      return null;
  }
}

// ── Model matching (prefix match like tracker) ─────────────

function findModelPricing(
  models: ModelPricingRecord[],
  sessionModel: string,
): ModelPricingRecord | undefined {
  // Exact match first
  const exact = models.find((m) => m.id === sessionModel);
  if (exact) return exact;
  // Prefix match: session model "claude-opus-4-5-20251101" starts with pricing id "claude-opus-4-5"
  return models.find((m) => sessionModel.startsWith(m.id));
}

// ── Pure computation ───────────────────────────────────────

export function computeSpend(
  sessions: SessionRecord[],
  models: ModelPricingRecord[],
  periodFilter?: string,
): SpendResult {
  let filtered = sessions;
  let period: string | null = periodFilter ?? null;

  if (periodFilter) {
    const periodMs = parsePeriodMs(periodFilter);
    if (periodMs !== null) {
      const cutoff = Date.now() - periodMs;
      filtered = sessions.filter((s) => s.sessionStart >= cutoff);
    }
  }

  let totalInputCost = 0;
  let totalOutputCost = 0;
  let totalCacheCost = 0;

  const modelMap = new Map<
    string,
    { name: string; cost: number; sessions: number; inputTokens: number; outputTokens: number }
  >();
  const featureMap = new Map<
    string,
    { cost: number; sessions: number }
  >();
  const unknownModels = new Set<string>();

  for (const session of filtered) {
    const pricing = findModelPricing(models, session.model);

    let inputCost = 0;
    let outputCost = 0;
    let cacheCost = 0;

    if (pricing) {
      inputCost = (session.inputTokens * pricing.input_per_mtok) / 1_000_000;
      outputCost = (session.outputTokens * pricing.output_per_mtok) / 1_000_000;
      cacheCost = (session.cacheReadTokens * pricing.cache_read_per_mtok) / 1_000_000;
    } else {
      unknownModels.add(session.model);
    }

    const sessionCost = inputCost + outputCost + cacheCost;
    totalInputCost += inputCost;
    totalOutputCost += outputCost;
    totalCacheCost += cacheCost;

    // Group by model (use pricing id if found, otherwise raw model string)
    const modelKey = pricing?.id ?? session.model;
    const modelEntry = modelMap.get(modelKey) ?? {
      name: pricing?.name ?? session.model,
      cost: 0,
      sessions: 0,
      inputTokens: 0,
      outputTokens: 0,
    };
    modelEntry.cost += sessionCost;
    modelEntry.sessions += 1;
    modelEntry.inputTokens += session.inputTokens;
    modelEntry.outputTokens += session.outputTokens;
    modelMap.set(modelKey, modelEntry);

    // Group by feature
    if (session.featureId) {
      const featureEntry = featureMap.get(session.featureId) ?? {
        cost: 0,
        sessions: 0,
      };
      featureEntry.cost += sessionCost;
      featureEntry.sessions += 1;
      featureMap.set(session.featureId, featureEntry);
    }
  }

  return {
    total: totalInputCost + totalOutputCost + totalCacheCost,
    inputCost: totalInputCost,
    outputCost: totalOutputCost,
    cacheCost: totalCacheCost,
    sessionCount: filtered.length,
    period,
    byModel: Array.from(modelMap.entries()).map(([model, data]) => ({
      model,
      name: data.name,
      cost: data.cost,
      sessions: data.sessions,
      inputTokens: data.inputTokens,
      outputTokens: data.outputTokens,
    })),
    byFeature: Array.from(featureMap.entries()).map(([featureId, data]) => ({
      featureId,
      cost: data.cost,
      sessions: data.sessions,
    })),
  };
}

// ── CLI Command ────────────────────────────────────────────

function formatDollars(amount: number): string {
  return amount < 0.01 && amount > 0 ? `$${amount.toFixed(4)}` : `$${amount.toFixed(2)}`;
}

export const spendCommand = new Command("spend")
  .description("Show AI spend from tracking data")
  .option("--period <period>", "Filter by time period (e.g., 7d, 30d, 2w, 1m)")
  .option("--json", "Output as JSON")
  .action((options: { period?: string; json?: boolean }) => {
    const summaryPath = resolve(
      process.cwd(),
      ".fathom",
      "tracking",
      "summary.json",
    );

    if (!existsSync(summaryPath)) {
      console.log(
        chalk.yellow(
          "No tracking data found. Run `fathom track` first to record session data.",
        ),
      );
      return;
    }

    let summary: { sessions: SessionRecord[] };
    try {
      summary = JSON.parse(readFileSync(summaryPath, "utf-8"));
    } catch {
      console.log(
        chalk.red("Failed to parse tracking data. Try running `fathom track --backfill` to rebuild."),
      );
      return;
    }

    const data = loadData();
    const result = computeSpend(summary.sessions, data.models, options.period);

    // Warn about unknown models
    const unknownModels = new Set<string>();
    for (const session of summary.sessions) {
      if (!findModelPricing(data.models, session.model)) {
        unknownModels.add(session.model);
      }
    }
    for (const model of unknownModels) {
      warnAction(
        `Unknown model "${model}"`,
        "No pricing data available, cost shown as $0",
        "Update data/models.yaml or report this model",
      );
    }

    if (options.json) {
      process.stdout.write(JSON.stringify(result, null, 2) + "\n");
      return;
    }

    // Display header
    const periodLabel = options.period ? ` (last ${options.period})` : "";
    console.log(chalk.bold(`\nFathom — Spend${periodLabel}`));
    console.log(chalk.dim("─".repeat(65)));

    if (result.sessionCount === 0) {
      console.log(chalk.dim("No sessions found for the selected period.\n"));
      return;
    }

    // Summary line
    console.log(
      `Total: ${chalk.green(formatDollars(result.total))} across ${result.sessionCount} session${result.sessionCount !== 1 ? "s" : ""}`,
    );
    console.log();

    // Table 1 — By Model
    const modelTable = new Table({
      head: [
        chalk.white("Model"),
        chalk.white("Sessions"),
        chalk.white("Input $"),
        chalk.white("Output $"),
        chalk.white("Cache $"),
        chalk.white("Total $"),
      ],
      colAligns: ["left", "center", "right", "right", "right", "right"] as Array<"left" | "center" | "right">,
    });

    for (const m of result.byModel.sort((a, b) => b.cost - a.cost)) {
      const inputCostForModel = (m.inputTokens * (data.models.find((p) => p.id === m.model)?.input_per_mtok ?? 0)) / 1_000_000;
      const outputCostForModel = (m.outputTokens * (data.models.find((p) => p.id === m.model)?.output_per_mtok ?? 0)) / 1_000_000;
      const cacheCostForModel = m.cost - inputCostForModel - outputCostForModel;

      modelTable.push([
        m.name,
        m.sessions.toString(),
        m.cost > 0 ? chalk.green(formatDollars(inputCostForModel)) : chalk.dim("$0.00"),
        m.cost > 0 ? chalk.green(formatDollars(outputCostForModel)) : chalk.dim("$0.00"),
        cacheCostForModel > 0 ? chalk.green(formatDollars(cacheCostForModel)) : chalk.dim("$0.00"),
        m.cost > 0 ? chalk.green(formatDollars(m.cost)) : chalk.dim("$0.00"),
      ]);
    }

    console.log(modelTable.toString());

    // Table 2 — By Feature (only if any sessions have featureId)
    if (result.byFeature.length > 0) {
      console.log();
      const featureTable = new Table({
        head: [
          chalk.white("Feature"),
          chalk.white("Sessions"),
          chalk.white("Cost"),
        ],
        colAligns: ["left", "center", "right"] as Array<"left" | "center" | "right">,
      });

      for (const f of result.byFeature.sort((a, b) => b.cost - a.cost)) {
        featureTable.push([
          f.featureId,
          f.sessions.toString(),
          f.cost > 0 ? chalk.green(formatDollars(f.cost)) : chalk.dim("$0.00"),
        ]);
      }

      console.log(featureTable.toString());
    }

    console.log();
  });
